import React, { Component, ErrorInfo, ReactNode } from "react";
import styled from "styled-components";

// Define the type for the props and state
interface ErrorBoundaryProps {
  children: ReactNode;
}

interface ErrorBoundaryState {
  hasError: boolean;
}

class ErrorBoundary extends Component<ErrorBoundaryProps, ErrorBoundaryState> {
  constructor(props: ErrorBoundaryProps) {
    super(props);
    this.state = { hasError: false };
  }

  static getDerivedStateFromError(): ErrorBoundaryState {
    // Update state so the next render will show the fallback UI
    return { hasError: true };
  }

  componentDidCatch(error: Error, info: ErrorInfo): void {
    // Log the error to an error reporting service
    console.error("Error occurred:", error);
    console.error("Error info:", info);
  }

  handleContactClick = () => {
    // Navigate programmatically
    window.location.href = "/contact";
  };

  render() {
    if (this.state.hasError) {
      // Render fallback UI
      return (
        <ErrorContainer>
          <ErrorHeading>Something went wrong!</ErrorHeading>
          <ErrorMessage>
            Please try again later. If this issue persists,{" "}
            <ContactLink
              href="/contact"
              onClick={(e) => {
                e.preventDefault();
                this.handleContactClick();
              }}
            >
              contact
            </ContactLink>{" "}
            our support team for assistance
          </ErrorMessage>
          <ErrorImageContainer>
            <ErrorImage src="/images/404.png" alt="Error 404" />
          </ErrorImageContainer>
        </ErrorContainer>
      );
    }

    // Render children if no error
    return this.props.children;
  }
}

// Styled components for the error page
const ErrorContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  text-align: center;
  padding: 2rem;
  max-width: 800px;
  margin: 0 auto;
`;

const ErrorHeading = styled.h1`
  color: #00b87c;
  font-size: 2.5rem;
  margin-bottom: 1rem;
  font-weight: 600;
`;

const ErrorMessage = styled.div`
  font-size: 1.5rem;
  color: #333;
  margin-bottom: 2rem;
`;

const ContactLink = styled.a`
  color: #0066cc;
  text-decoration: underline;
  cursor: pointer;
  &:hover {
    color: #004499;
  }
`;

const ErrorImageContainer = styled.div`
  max-width: 400px;
  margin-top: 1rem;
`;

const ErrorImage = styled.img`
  width: 100%;
  height: auto;
`;

export default ErrorBoundary;
